import { CustomAttribute, EventI, EventProfile, EventProfileReservation } from "../../../../pojo/EventI";
import { CellData, RowData } from "../../../s25-spreadsheet/types/sheet.js.types";
import { S25Util } from "../../../../util/s25-util";
import { Event } from "../../../../pojo/Event";
import { Contact } from "../../../../pojo/Contact";
import { ImportUtil } from "../import.util";

export class EventSpreadsheetUtil {
    public static rowsToEvents = (
        extUidToEvent: Map<string, EventI>,
        extUidToProfile: Map<string, EventProfile>,
        contEmailSet: Set<string>,
        locNameSet: Set<string>,
        resNameSet: Set<string>,
        errorSet: Set<string>,
        rows: RowData[],
        rowsProcessedPromise: Promise<void>,
        eventTypeNameMap: Map<string, number>,
        orgNameMap: Map<string, number>,
        catNameMap: Map<string, number>,
        custAttrNameMap: Map<string, CustomAttribute>,
        contNameMapPromise: Promise<Map<string, number>>,
        locNameMapPromise: Promise<Map<string, number>>,
        resNameMapPromise: Promise<Map<string, number>>,
        postProcessPromises: Promise<unknown>[],
    ) => {
        let events: EventI[] = [];
        let cellNumToHeader: Map<number, String> = new Map<number, String>();

        if (!rows || !rows.length) {
            return [];
        }

        for (let r = 0; r < rows.length; r++) {
            if (!rows[r] || !rows[r].cells || !rows[r].cells.length) {
                continue;
            }

            if (r === 0) {
                // headers
                for (let c = 0; c < rows[r].cells.length; c++) {
                    let cell = rows[r].cells[c];
                    cellNumToHeader.set(c, cell.text);
                }
                continue;
            }

            let event = this.cellsToEvent(
                extUidToEvent,
                extUidToProfile,
                contEmailSet,
                locNameSet,
                resNameSet,
                errorSet,
                rows[r].cells,
                cellNumToHeader,
                rowsProcessedPromise,
                eventTypeNameMap,
                orgNameMap,
                catNameMap,
                custAttrNameMap,
                contNameMapPromise,
                locNameMapPromise,
                resNameMapPromise,
                postProcessPromises,
            );
            if (event && !events.includes(event)) {
                events.push(event);
            }
        }

        return events;
    };

    public static cellsToEvent = (
        extUidToEvent: Map<string, EventI>,
        extUidToProfile: Map<string, EventProfile>,
        contEmailSet: Set<string>,
        locNameSet: Set<string>,
        resNameSet: Set<string>,
        errorSet: Set<string>,
        cells: CellData[],
        cellNumToHeader: Map<number, String>,
        rowsProcessedPromise: Promise<void>,
        eventTypeNameMap: Map<string, number>,
        orgNameMap: Map<string, number>,
        catNameMap: Map<string, number>,
        custAttrNameMap: Map<string, CustomAttribute>,
        contNameMapPromise: Promise<Map<string, number>>,
        locNameMapPromise: Promise<Map<string, number>>,
        resNameMapPromise: Promise<Map<string, number>>,
        postProcessPromises: Promise<unknown>[],
    ): EventI | null => {
        // get EVENT_ID value
        let eventIdCell = cells.find(
            (c, i) => ImportUtil.normalize(cellNumToHeader.get(i)) === ImportUtil.normalize("EVENT_ID"),
        );
        if (!eventIdCell || !eventIdCell.text) {
            errorSet.add("Missing EVENT_ID");
            return;
        }
        let eventId = S25Util.toStr(eventIdCell.text).trim();

        let profileIdCell = cells.find(
            (c, i) => ImportUtil.normalize(cellNumToHeader.get(i)) === ImportUtil.normalize("PROFILE_ID"),
        );
        if (!profileIdCell || !profileIdCell.text) {
            errorSet.add("Missing PROFILE_ID");
            return;
        }
        let profileId = S25Util.toStr(profileIdCell.text).trim();

        let event: EventI;
        if (extUidToEvent.has(eventId)) {
            event = extUidToEvent.get(eventId);
        } else {
            event = {
                alien_uid: eventId,
                status: "new",
                state: Event.State.Ids.Tentative,
                relatedEvents: [],
                profile: [],
            };
            extUidToEvent.set(eventId, event);
        }

        let profile: EventProfile;
        if (extUidToProfile.has(profileId)) {
            profile = extUidToProfile.get(profileId);
        } else {
            profile = {
                reservation: [{ space_reservation: [], resource_reservation: [] }],
                ad_hoc_datelist: [],
                binding_reservation: [],
            };
            extUidToProfile.set(profileId, profile);
            event.profile.push(profile);
        }

        cells.forEach((cell, index) => {
            const rawHeader = cellNumToHeader.get(index) || "";
            const normalizedHeader = ImportUtil.normalize(rawHeader);
            let value = cell.text?.trim();
            if (!value) return;

            if (normalizedHeader === ImportUtil.normalize("EVENT_ID")) {
                event.alien_uid = value;
            } else if (normalizedHeader === ImportUtil.normalize("EVENT_NAME")) {
                event.event_name = value;
            } else if (normalizedHeader === ImportUtil.normalize("EVENT_TITLE")) {
                event.event_title = value;
            } else if (normalizedHeader === ImportUtil.normalize("DESCRIPTION")) {
                event.event_text = event.event_text || [];
                if (!event.event_text.find((t) => t.text_type_id === 1)) {
                    event.event_text.push({
                        text_type_id: 1,
                        text: value,
                    });
                }
            } else if (normalizedHeader === ImportUtil.normalize("INTERNAL_NOTES")) {
                event.event_text = event.event_text || [];
                if (!event.event_text.find((t) => t.text_type_id === 2)) {
                    event.event_text.push({
                        text_type_id: 2,
                        text: value,
                    });
                }
            } else if (normalizedHeader === ImportUtil.normalize("EVENT_TYPE")) {
                if (S25Util.isInt(value)) {
                    event.event_type_id = parseInt(value);
                } else {
                    value = value.replace(/^\$NAME-/, "");
                    postProcessPromises.push(
                        rowsProcessedPromise.then(async () => {
                            event.event_type_id = eventTypeNameMap.get(value);
                            if (!event.event_type_id) {
                                errorSet.add(`Event type not found: [${value}]`);
                            }
                        }),
                    );
                }
            } else if (normalizedHeader === ImportUtil.normalize("PRIMARY_ORGANIZATION")) {
                if (S25Util.isInt(value)) {
                    event.organization = event.organization || [];
                    if (!event.organization.find((o) => o.primary === "T")) {
                        event.organization.push({ organization_id: parseInt(value), primary: "T" });
                    }
                } else {
                    value = value.replace(/^\$NAME-/, "");
                    postProcessPromises.push(
                        rowsProcessedPromise.then(async () => {
                            let orgId = orgNameMap.get(value);
                            if (!orgId) {
                                errorSet.add(`Organization not found: [${value}]`);
                            } else {
                                event.organization = event.organization || [];
                                if (!event.organization.find((o) => o.primary === "T")) {
                                    event.organization.push({ organization_id: orgId, primary: "T" });
                                }
                            }
                        }),
                    );
                }
            } else if (normalizedHeader === ImportUtil.normalize("REG_HEADCOUNT")) {
                event.reg_head_count = parseInt(value) || 0;
                profile.registered_count = event.reg_head_count;
            } else if (normalizedHeader === ImportUtil.normalize("EXP_HEADCOUNT")) {
                event.exp_head_count = parseInt(value) || 0;
                profile.expected_count = event.exp_head_count;
            } else if (normalizedHeader === ImportUtil.normalize("CATEGORIES")) {
                event.category = event.category || [];
                S25Util.array.unique(ImportUtil.splitStr(value, "|")).forEach((c) => {
                    if (S25Util.isInt(c)) {
                        if (!event.category.find((cat) => cat.category_id === parseInt(c))) {
                            event.category.push({ category_id: parseInt(c) });
                        }
                    } else {
                        c = c.replace(/^\$NAME-/, "");
                        postProcessPromises.push(
                            rowsProcessedPromise.then(async () => {
                                let catId = catNameMap.get(c);
                                if (!catId) {
                                    errorSet.add(`Category not found: [${c}]`);
                                } else {
                                    if (!event.category.find((cat) => cat.category_id === catId)) {
                                        event.category.push({ category_id: catId });
                                    }
                                }
                            }),
                        );
                    }
                });
            } else if (normalizedHeader === ImportUtil.normalize("INSTRUCTOR")) {
                this.addContactRole(
                    contEmailSet,
                    errorSet,
                    event,
                    Contact.Role.Event.id.Instructor,
                    value,
                    rowsProcessedPromise,
                    contNameMapPromise,
                    postProcessPromises,
                );
            } else if (normalizedHeader === ImportUtil.normalize("SCHEDULER")) {
                this.addContactRole(
                    contEmailSet,
                    errorSet,
                    event,
                    Contact.Role.Event.id.Scheduler,
                    value,
                    rowsProcessedPromise,
                    contNameMapPromise,
                    postProcessPromises,
                );
            } else if (normalizedHeader === ImportUtil.normalize("REQUESTOR")) {
                this.addContactRole(
                    contEmailSet,
                    errorSet,
                    event,
                    Contact.Role.Event.id.Requestor,
                    value,
                    rowsProcessedPromise,
                    contNameMapPromise,
                    postProcessPromises,
                );
            } else if (normalizedHeader === ImportUtil.normalize("PROFILE_ID")) {
                profile.ext_uid = value;
            } else if (normalizedHeader === ImportUtil.normalize("PROFILE_NAME")) {
                profile.profile_name = value;
            } else if (normalizedHeader === ImportUtil.normalize("FIRST_OCCURRENCE_DATE")) {
                profile.init_start_dt = value;
            } else if (normalizedHeader === ImportUtil.normalize("PRE_EVENT_TIME")) {
                profile.pre_event = value;
            } else if (normalizedHeader === ImportUtil.normalize("START_TIME")) {
                event.start_date = value;
            } else if (normalizedHeader === ImportUtil.normalize("END_TIME")) {
                event.end_date = value;
            } else if (normalizedHeader === ImportUtil.normalize("POST_EVENT_TIME")) {
                profile.post_event = value;
            } else if (normalizedHeader === ImportUtil.normalize("REPEATING_RULE")) {
                profile.profile_code = value;
            } else if (normalizedHeader === ImportUtil.normalize("ADHOC_DATES")) {
                S25Util.array.unique(ImportUtil.splitStr(value, "|")).forEach((d) => {
                    profile.ad_hoc_datelist.push({
                        ad_hoc_start_dt: d,
                    });
                });
            } else if (normalizedHeader === ImportUtil.normalize("LOCATIONS")) {
                let reservation: EventProfileReservation = profile.reservation[0];
                S25Util.array.unique(ImportUtil.splitStr(value, "|")).forEach((l) => {
                    let shared = l.endsWith(":shared") ? "T" : "F";
                    l = l.replace(/:shared$/, "");
                    l = l.replace(/:unshared$/, "");
                    if (S25Util.isInt(l)) {
                        reservation.space_reservation.push({ space_id: parseInt(l), share: shared });
                    } else {
                        l = l.replace(/^\$NAME-/, "");
                        locNameSet.add(l);
                        postProcessPromises.push(
                            rowsProcessedPromise.then(async () => {
                                const map = await locNameMapPromise;
                                let locId = map.get(l);
                                if (!locId) {
                                    errorSet.add(`Location not found: [${l}]`);
                                } else {
                                    reservation.space_reservation.push({ space_id: locId, share: shared });
                                }
                            }),
                        );
                    }
                });
            } else if (normalizedHeader === ImportUtil.normalize("RESOURCES")) {
                let reservation: EventProfileReservation = profile.reservation[0];
                S25Util.array.unique(ImportUtil.splitStr(value, "|")).forEach((r) => {
                    let quantity = 1;
                    let parts = r.split(":");
                    if (parts.length > 1 && S25Util.isInt(parts[parts.length - 1])) {
                        quantity = parseInt(parts.pop() || "1");
                        r = parts.join(":");
                    }
                    if (S25Util.isInt(r)) {
                        reservation.resource_reservation.push({ resource_id: parseInt(r), quantity: quantity });
                    } else {
                        r = r.replace(/^\$NAME-/, "");
                        resNameSet.add(r);
                        postProcessPromises.push(
                            rowsProcessedPromise.then(async () => {
                                const map = await resNameMapPromise;
                                let resId = map.get(r);
                                if (!resId) {
                                    errorSet.add(`Resource not found: [${r}]`);
                                } else {
                                    reservation.resource_reservation.push({ resource_id: resId, quantity: quantity });
                                }
                            }),
                        );
                    }
                });
            } else if (normalizedHeader === ImportUtil.normalize("BOUND_PROFILE_ID")) {
                ImportUtil.splitStr(value, "|").forEach((p) => {
                    profile.binding_reservation.push({
                        bound_reservation_ext_uid: p,
                    });
                });
            } else if (normalizedHeader === ImportUtil.normalize("RELATED_EVENT_ID")) {
                S25Util.array.unique(ImportUtil.splitStr(value, "|")).forEach((r) => {
                    event.relatedEvents.push({
                        alien_uid: r,
                    });
                });
            } else if (normalizedHeader === ImportUtil.normalize("CUSTOM_ATTRIBUTES")) {
                ImportUtil.splitStr(value, "|").forEach((c) => {
                    let parts = c.split(":");
                    if (parts.length < 2) {
                        errorSet.add(`Custom attribute missing value: [${c}]`);
                        return;
                    }
                    let custAttrName = parts.shift();
                    let custAttrValue = parts.join(":");
                    if (S25Util.isInt(custAttrName)) {
                        let attrId = parseInt(custAttrName);
                        event.custom_attribute = event.custom_attribute || [];
                        if (!event.custom_attribute.find((c) => c.attribute_id === attrId)) {
                            let custAttr = Array.from(custAttrNameMap.values()).find(
                                (attr) => attr.attribute_id === attrId,
                            );
                            if (custAttr) {
                                event.custom_attribute.push({
                                    attribute_id: attrId,
                                    attribute_type: custAttr.attribute_type,
                                    attribute_value: custAttrValue,
                                });
                            }
                        }
                    } else {
                        custAttrName = custAttrName.replace(/^\$NAME-/, "");
                        postProcessPromises.push(
                            rowsProcessedPromise.then(async () => {
                                let custAttr = custAttrNameMap.get(custAttrName);
                                if (!custAttr) {
                                    errorSet.add(`Custom attribute not found: [${custAttrName}]`);
                                } else {
                                    event.custom_attribute = event.custom_attribute || [];
                                    if (custAttr.attribute_type === "B") {
                                        custAttrValue = "" + S25Util.toBool(custAttrValue);
                                    } else if (custAttr.attribute_type === "T") {
                                        if (!custAttrValue?.startsWith("1900-01-01T")) {
                                            custAttrValue = "1900-01-01T" + custAttrValue;
                                        }
                                    }
                                    if (!event.custom_attribute.find((c) => c.attribute_id === custAttr.attribute_id)) {
                                        event.custom_attribute.push({
                                            attribute_id: custAttr.attribute_id,
                                            attribute_type: custAttr.attribute_type,
                                            attribute_value: custAttrValue,
                                        });
                                    }
                                }
                            }),
                        );
                    }
                });
            }
        });
        return event;
    };

    public static addContactRole(
        contEmailSet: Set<string>,
        errorSet: Set<string>,
        event: EventI,
        roleId: number,
        contactValue: string,
        rowsProcessedPromise: Promise<void>,
        contNameMapPromise: Promise<Map<string, number>>,
        postProcessPromises: Promise<unknown>[],
    ) {
        if (S25Util.isInt(contactValue)) {
            event.role = event.role || [];
            if (!event.role.find((r) => r.role_id === roleId)) {
                event.role.push({ role_id: roleId, contact: { contact_id: parseInt(contactValue) } });
            }
        } else {
            contactValue = contactValue.replace(/^\$NAME-/, "");
            contEmailSet.add(contactValue);
            postProcessPromises.push(
                rowsProcessedPromise.then(async () => {
                    const map = await contNameMapPromise;
                    let contId = map.get(contactValue);
                    if (!contId) {
                        errorSet.add(`Contact not found: [${contactValue}]`);
                    } else {
                        event.role = event.role || [];
                        if (!event.role.find((r) => r.role_id === roleId)) {
                            event.role.push({ role_id: roleId, contact: { contact_id: contId } });
                        }
                    }
                }),
            );
        }
    }
}
